import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { doPost, doFetch, doPut, doDelete, clearMyTour, s3upload } from '../../../redux/api/actions';
import { CREATE_TOUR, GET_MYTOUR, EDIT_TOUR, DELETE_TOUR, GET_PRESIGNED_IMAGE_URLS } from '../../../redux/api/constants';
import { Link, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import Layout from "../../layout";
import ImageUploader from "../../imageUploader";
import { useTranslation } from 'react-i18next';

const costOptions = ["PER_PERSON", "PER_TOUR"];

function EditTour() {
    const { t } = useTranslation();
    const { tourId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tour = useSelector(state => state.API.myTour);
    
    const [title, setTitle] = useState('');
    const [titleError, setTitleError] = useState('');
    const [description, setDescription] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const [duration, setDuration] = useState('');
    const [durationError, setDurationError] = useState('');
    const [cost, setCost] = useState('');
    const [costError, setCostError] = useState('');
    const [costType, setCostType] = useState('PER_PERSON');
    const [maxTourSize, setMaxTourSize] = useState('');
    const [maxTourSizeError, setMaxTourSizeError] = useState('');
    const [imageUrls, setImageUrls] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);

    const accessToken = useSelector(state => state.Auth.accessToken);
    const presignedImageUrls = useSelector(state => state.API.presignedImageUrls);

    useEffect(() => {
        if (tourId) {
            dispatch(doFetch(GET_MYTOUR, `tourguide/tours/${tourId}`, accessToken));
        } else {
            dispatch(clearMyTour());
        }
    }, [accessToken, tourId, dispatch]);

    useEffect(() => {
        if (tour) {
            setTitle(tour.title || '');
            setDescription(tour.description || '');
            setCost(tour.cost || '');
            setDuration(tour.duration_in_hours || '');
            setCostType(tour.cost_type || 'PER_PERSON');
            setMaxTourSize(tour.max_tour_size || '');
            setImageUrls(tour.image_urls || []);
        } else {
            setTitle('');
            setDescription('');
            setDuration('');
            setCost('');
            setCostType('PER_PERSON');
            setMaxTourSize('');
            setImageUrls([]);
        }
    }, [tour]);

    useEffect(() => {
        if (presignedImageUrls && presignedImageUrls.length > 0) {
            const processedImageUrls = presignedImageUrls.map(url => url.split('?')[0]);
            setImageUrls(processedImageUrls.join(','));
            dispatch(s3upload(imageFiles, presignedImageUrls));
        }
    }, [presignedImageUrls, dispatch, imageFiles]);

    const deleteTour = () => {
        dispatch(doDelete(DELETE_TOUR, `tourguide/tours/${tourId}`, accessToken, navigate));
    };

    const validateAndSubmit = (e) => {
        e.preventDefault();
        
        // Clear errors
        setTitleError('');
        setDescriptionError('');
        setDurationError('');
        setCostError('');
        setMaxTourSizeError('');

        let errors = 0;
        
        if (!title.trim()) {
            setTitleError('Title is required');
            errors++;
        }
        if (!description.trim()) {
            setDescriptionError('Description is required');
            errors++;
        }
        if (!duration || duration < 1) {
            setDurationError('Duration must be at least 1 hour');
            errors++;
        }
        if (!cost || cost < 0) {
            setCostError('Please enter a valid price');
            errors++;
        }
        if (!maxTourSize || maxTourSize < 1) {
            setMaxTourSizeError('Group size must be at least 1');
            errors++;
        }

        if (errors > 0) return;

        const params = {
            title,
            description,
            duration_in_hours: parseInt(duration),
            cost: parseFloat(cost),
            cost_type: costType,
            max_tour_size: parseInt(maxTourSize),
            image_urls: imageUrls && imageUrls.length > 0 ? imageUrls : "",
            // Keep these for backwards compatibility with existing API
            flavor: "SMALL",
            adventure_size: "IN_BETWEEN"
        };

        if (tourId) {
            dispatch(doPut(EDIT_TOUR, `tourguide/tours/${tourId}`, accessToken, params, navigate));
        } else {
            dispatch(doPost(CREATE_TOUR, "tourguide/tours", accessToken, params, navigate));
        }
    };

    const imageUploadFunc = (files) => {
        const fileNames = Array.from(files).map((file) => file.name);
        dispatch(doPost(GET_PRESIGNED_IMAGE_URLS, "tourguide/presigned-urls", accessToken, { images: fileNames }));
        setImageFiles(files);
    };

    return (
        <Layout selected="tourguide">
            <div className="tour-editor">
                <nav aria-label="breadcrumb" className="breadcrumb-nav">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/">Home</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <Link to="/tourguide#tab2">{t('tourguide-my-tours')}</Link>
                        </li>
                        <li className="breadcrumb-item active">
                            {tourId ? 'Edit Tour' : 'Create Tour'}
                        </li>
                    </ol>
                </nav>

                <div className="editor-header">
                    <h1>{tourId ? 'Edit Tour' : 'Create New Tour'}</h1>
                    <p className="subtitle">Fill in the details below to {tourId ? 'update your' : 'create a new'} tour</p>
                </div>

                <form onSubmit={validateAndSubmit} className="tour-form">
                    <div className="form-section">
                        <label className="form-section-title">Tour Images</label>
                        <ImageUploader
                            imageUrls={imageUrls && typeof imageUrls === 'string' ? imageUrls.split(',') : null}
                            imageUploadFunc={imageUploadFunc}
                            multiple={true}
                        />
                    </div>

                    <div className="form-section">
                        <label htmlFor="title" className="form-label">{t('tour-title')}</label>
                        <input
                            type="text"
                            id="title"
                            className={`form-input ${titleError ? 'error' : ''}`}
                            placeholder="e.g., Hidden Gems Walking Tour"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        {titleError && <span className="error-text">{titleError}</span>}
                    </div>

                    <div className="form-section">
                        <label htmlFor="description" className="form-label">{t('tour-description')}</label>
                        <textarea
                            id="description"
                            className={`form-input textarea ${descriptionError ? 'error' : ''}`}
                            placeholder="Describe your tour experience, what visitors will see and do..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="4"
                        />
                        {descriptionError && <span className="error-text">{descriptionError}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-section half">
                            <label htmlFor="duration" className="form-label">{t('tour-duration')}</label>
                            <input
                                type="number"
                                id="duration"
                                className={`form-input ${durationError ? 'error' : ''}`}
                                placeholder="2"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                                min="1"
                            />
                            {durationError && <span className="error-text">{durationError}</span>}
                        </div>

                        <div className="form-section half">
                            <label htmlFor="maxTourSize" className="form-label">{t('tour-max-size')}</label>
                            <input
                                type="number"
                                id="maxTourSize"
                                className={`form-input ${maxTourSizeError ? 'error' : ''}`}
                                placeholder="8"
                                value={maxTourSize}
                                onChange={(e) => setMaxTourSize(e.target.value)}
                                min="1"
                            />
                            {maxTourSizeError && <span className="error-text">{maxTourSizeError}</span>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-section half">
                            <label htmlFor="cost" className="form-label">{t('tour-cost')}</label>
                            <div className="input-with-prefix">
                                <span className="prefix">$</span>
                                <input
                                    type="number"
                                    id="cost"
                                    className={`form-input ${costError ? 'error' : ''}`}
                                    placeholder="50"
                                    value={cost}
                                    onChange={(e) => setCost(e.target.value)}
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                            {costError && <span className="error-text">{costError}</span>}
                        </div>

                        <div className="form-section half">
                            <label htmlFor="costType" className="form-label">{t('tour-cost-type')}</label>
                            <select
                                id="costType"
                                className="form-input"
                                value={costType}
                                onChange={(e) => setCostType(e.target.value)}
                            >
                                {costOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option === 'PER_PERSON' ? t('tour-per-person') : t('tour-per-tour')}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn-primary">
                            {tourId ? t('tour-update') : t('tour-create')}
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => navigate("/tourguide#tab2")}>
                            Cancel
                        </button>
                        {tourId && (
                            <>
                                <Link to={`/tourguide/tours/${tourId}/schedule`} className="btn-outline">
                                    {t('tour-schedule')}
                                </Link>
                                <button type="button" className="btn-danger" onClick={() => setShowModal(true)}>
                                    {t('tour-delete')}
                                </button>
                            </>
                        )}
                    </div>
                </form>

                {/* Delete Confirmation Modal */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h3>Delete Tour?</h3>
                            <p>This action cannot be undone. Are you sure you want to delete this tour?</p>
                            <div className="modal-actions">
                                <button className="btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button className="btn-danger" onClick={deleteTour}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default EditTour;
